import qs from 'qs';

import type { definitions, paths } from '@luxuryescapes/contract-svc-lux-loyalty';

import type {
  CLAIM_ACTION_STATUSES,
  CLAIM_ACTION_TYPES,
  CLAIM_CAMPAIGN_EMAILS,
  CLAIM_CODE_STATUSES,
  CLAIM_MATCH_CHECK_TYPES,
  CLAIM_RECORD_STATUSES,
  CLAIM_TARGET_TIERS,
  DENY_REASONS,
  EVIDENCE_FIELD_TYPES,
  EXTERNAL_API_METHODS,
} from '~/consts/luxLoyalty';

import { request } from '../common';

import { BASE_PATH } from './constants';

// ---------------------------------------------------------------------------
// Contract types
// ---------------------------------------------------------------------------

/** A claim campaign exactly as the contract presents it. See {@link ClaimCampaign} for the stricter local shape. */
export type ClaimCampaignContract = definitions['claimCampaignPresenter'];

type ListClaimCampaignsContractResponse =
  paths['/api/lux-loyalty/claims/campaigns/all']['get']['responses']['200']['content']['application/json'];
type CreateClaimCampaignContractResponse =
  paths['/api/lux-loyalty/claims/campaigns']['post']['responses']['201']['content']['application/json'];
type GetClaimCampaignContractResponse =
  paths['/api/lux-loyalty/claims/campaigns/{campaignId}']['get']['responses']['200']['content']['application/json'];
type UpdateClaimCampaignContractResponse =
  paths['/api/lux-loyalty/claims/campaigns/{campaignId}']['patch']['responses']['200']['content']['application/json'];

export type ClaimCampaignStatus = ClaimCampaignContract['status'];
export type ClaimCampaignType = ClaimCampaignContract['type'];
export type ClaimValidatorType = ClaimCampaignContract['validatorType'];

export type ClaimMatchCheckType = (typeof CLAIM_MATCH_CHECK_TYPES)[number];
export type EvidenceFieldType = (typeof EVIDENCE_FIELD_TYPES)[number];
export type ClaimActionType = (typeof CLAIM_ACTION_TYPES)[number];
export type DenyReason = (typeof DENY_REASONS)[number];
export type ClaimCodeStatus = (typeof CLAIM_CODE_STATUSES)[number];
export type ClaimRecordStatus = (typeof CLAIM_RECORD_STATUSES)[number];
export type ClaimActionStatus = (typeof CLAIM_ACTION_STATUSES)[number];
export type ClaimTargetTier = (typeof CLAIM_TARGET_TIERS)[number];
export type ExternalApiMethod = (typeof EXTERNAL_API_METHODS)[number];
export type ClaimCampaignEmail = (typeof CLAIM_CAMPAIGN_EMAILS)[number];

/** Unfiltered campaign list, typed straight from the contract. */
export type ListCampaignsResponse = ListClaimCampaignsContractResponse;

export type AdminSubmitClaimRequestBody =
  paths['/api/lux-loyalty/claims/campaigns/{campaignId}/admin-claim']['post']['requestBody']['content']['application/json'];

export type AdminSubmitClaimResponse =
  paths['/api/lux-loyalty/claims/campaigns/{campaignId}/admin-claim']['post']['responses']['200']['content']['application/json'];

// ---------------------------------------------------------------------------
// Approval requests
// ---------------------------------------------------------------------------

export type ListApprovalRequestsResponse =
  paths['/api/lux-loyalty/claims/campaigns/{campaignId}/approval-requests']['get']['responses']['200']['content']['application/json'];

type ApprovalRequestSummary = ListApprovalRequestsResponse['result']['requests'][number];
type ApprovalRequestDetail =
  paths['/api/lux-loyalty/claims/approval-requests/{requestId}']['get']['responses']['200']['content']['application/json']['result'];

export type ApprovalRequest = ApprovalRequestSummary & Partial<Pick<ApprovalRequestDetail, 'attemptNumber'>>;

export type ApprovalRequestStatus = ApprovalRequest['status'];

export type ListApprovalRequestsParams =
  paths['/api/lux-loyalty/claims/campaigns/{campaignId}/approval-requests']['get']['parameters']['query'];

export type ApproveApprovalRequestBody =
  paths['/api/lux-loyalty/claims/approval-requests/{requestId}/approve']['post']['requestBody']['content']['application/json'];

export type ApproveApprovalRequestResponse =
  paths['/api/lux-loyalty/claims/approval-requests/{requestId}/approve']['post']['responses']['200']['content']['application/json'];

type ContractGetApprovalRequestResponse =
  paths['/api/lux-loyalty/claims/approval-requests/{requestId}']['get']['responses']['200']['content']['application/json'];

export type GetApprovalRequestResponse = Omit<ContractGetApprovalRequestResponse, 'result'> & {
  result: ApprovalRequestDetail;
};

export type RejectApprovalRequestBody =
  paths['/api/lux-loyalty/claims/approval-requests/{requestId}/reject']['post']['requestBody']['content']['application/json'];

export type RejectApprovalRequestResponse =
  paths['/api/lux-loyalty/claims/approval-requests/{requestId}/reject']['post']['responses']['200']['content']['application/json'];

// ---------------------------------------------------------------------------
// Evidence + match check config
// ---------------------------------------------------------------------------

/** A single evidence field definition for campaigns with requireApproval */
export interface EvidenceField {
  name: string;
  label: string;
  description?: string;
  type?: EvidenceFieldType;
  options?: Array<string>;
  multiple?: boolean;
}

/** Individual check definition within a multi-check config */
export interface MatchCheckDefinition {
  key: string;
  eligibilityTemplate: string;
}

/** Config for BQ_QUERY match check */
export interface BqQueryMatchCheckConfig {
  type: 'BQ_QUERY';
  /** SQL query - must SELECT customer_bk or customer_id, and filter with: WHERE customer_bk IN UNNEST(@customerIds) */
  bqQuery: string;
  checks: Array<MatchCheckDefinition>;
}

export type MatchCheckConfig = BqQueryMatchCheckConfig;

// ---------------------------------------------------------------------------
// Validator Config Types
// ---------------------------------------------------------------------------

/** Config for PREGEN_CODE validator (no extra fields needed) */
export interface PregenCodeValidatorConfig {
  type: 'PREGEN_CODE';
}

/** Config for SHARED_CODE validator (no extra fields needed) */
export interface SharedCodeValidatorConfig {
  type: 'SHARED_CODE';
}

/** Config for EXTERNAL_API validator */
export interface ExternalApiValidatorConfig {
  type: 'EXTERNAL_API';
  endpoint: string;
  method: ExternalApiMethod;
  headers?: Record<string, string>;
  requestTemplate?: Record<string, unknown>;
  successPath: string;
  errorPath?: string;
  timeout: number;
}

/** Config for DIRECT validator (no extra fields needed) */
export interface DirectValidatorConfig {
  type: 'DIRECT';
}

/** Config for ADMIN_ONLY validator — customers can't self-enrol; admins enrol them (no extra fields needed) */
export interface AdminOnlyValidatorConfig {
  type: 'ADMIN_ONLY';
}

export type ValidatorConfig =
  | PregenCodeValidatorConfig
  | SharedCodeValidatorConfig
  | ExternalApiValidatorConfig
  | DirectValidatorConfig
  | AdminOnlyValidatorConfig;

// ---------------------------------------------------------------------------
// Action Config Types
// ---------------------------------------------------------------------------

/** Config for TIER_UPGRADE action */
export interface TierUpgradeActionConfig {
  type: 'TIER_UPGRADE';
  targetTier: ClaimTargetTier;
  reasonCode: string;
  remarks: string;
}

/** Config for BONUS_POINTS action */
export interface BonusPointsActionConfig {
  type: 'BONUS_POINTS';
  bonusPoints: number;
  activityDescription: string;
}

/** Config for STATUS_CREDITS action */
export interface StatusCreditsActionConfig {
  type: 'STATUS_CREDITS';
  statusCredits: number;
  bonusStatusCredits?: number;
  activityDescription: string;
}

/** A single entry in the dynamic tier upgrade reward mapping */
export interface RewardMappingEntry {
  match: Record<string, string>;
  targetTier: ClaimTargetTier;
  remarks: string;
}

/** Config for DYNAMIC_TIER_UPGRADE action */
export interface DynamicTierUpgradeActionConfig {
  type: 'DYNAMIC_TIER_UPGRADE';
  rewardMapping: Array<RewardMappingEntry>;
  reasonCode?: string;
}

export type ActionConfig =
  | TierUpgradeActionConfig
  | BonusPointsActionConfig
  | StatusCreditsActionConfig
  | DynamicTierUpgradeActionConfig;

// ---------------------------------------------------------------------------
// Entity Types
// ---------------------------------------------------------------------------

/** A claim campaign with tighter types for the JSON columns than the contract gives. */
export type ClaimCampaign = Omit<ClaimCampaignContract, 'evidenceFields' | 'matchCheckConfig'> & {
  evidenceFields?: Array<EvidenceField> | null;
  matchCheckConfig?: MatchCheckConfig | null;
};

/** Code entity returned by the admin API */
export interface ClaimCode {
  id: string;
  code: string;
  status: ClaimCodeStatus;
  claimedBy?: string | null;
  claimedAt?: string | null;
  expiresAt?: string | null;
  maxRedemptions?: number | null;
  currentRedemptions: number;
  createdAt: string;
}

/** Individual action result within a claim record */
export interface ClaimActionRecord {
  id: string;
  actionType: ClaimActionType;
  status: ClaimActionStatus;
  result?: Record<string, unknown> | null;
  errorMessage?: string | null;
  executedAt?: string | null;
}

/** Claim record entity returned by the admin API */
export interface ClaimRecord {
  id: string;
  customerId: string;
  accountId: string;
  status: ClaimRecordStatus;
  validationInput?: Record<string, unknown> | null;
  validationResult?: Record<string, unknown> | null;
  errorMessage?: string | null;
  claimedAt?: string | null;
  createdAt: string;
  actions: ClaimActionRecord[];
}

// ---------------------------------------------------------------------------
// Request Params
// ---------------------------------------------------------------------------

/** Params for listing campaigns */
export type ListClaimCampaignsParams = paths['/api/lux-loyalty/claims/campaigns/all']['get']['parameters']['query'];

/** Body for creating a campaign */
export type CreateClaimCampaignBody =
  paths['/api/lux-loyalty/claims/campaigns']['post']['requestBody']['content']['application/json'];

/** Body for updating a campaign */
export type UpdateClaimCampaignBody =
  paths['/api/lux-loyalty/claims/campaigns/{campaignId}']['patch']['requestBody']['content']['application/json'];

/** Body for uploading codes */
export type UploadClaimCodesBody =
  paths['/api/lux-loyalty/claims/campaigns/{campaignId}/codes']['post']['requestBody']['content']['application/json'];

/** Params for listing codes */
export type ListClaimCodesParams =
  paths['/api/lux-loyalty/claims/campaigns/{campaignId}/codes']['get']['parameters']['query'];

/** Params for listing claim records */
export type ListClaimRecordsParams =
  paths['/api/lux-loyalty/claims/campaigns/{campaignId}/claims']['get']['parameters']['query'];

/** Body for the test-match-check dry run */
export type TestMatchCheckBody =
  paths['/api/lux-loyalty/claims/campaigns/test-match-check']['post']['requestBody']['content']['application/json'];

// ---------------------------------------------------------------------------
// Response Types
// ---------------------------------------------------------------------------

/** Response from listing campaigns */
export type ListClaimCampaignsResponse = Omit<ListClaimCampaignsContractResponse, 'result'> & {
  result: Omit<ListClaimCampaignsContractResponse['result'], 'campaigns'> & {
    campaigns: Array<ClaimCampaign>;
  };
};

/** Response from creating a campaign */
export type CreateClaimCampaignResponse = Omit<CreateClaimCampaignContractResponse, 'result'> & {
  result: Omit<CreateClaimCampaignContractResponse['result'], 'campaign'> & {
    campaign: ClaimCampaign;
  };
};

/** Response from getting a single campaign */
export type GetClaimCampaignResponse = Omit<GetClaimCampaignContractResponse, 'result'> & {
  result: Omit<GetClaimCampaignContractResponse['result'], 'campaign'> & {
    campaign: ClaimCampaign;
  };
};

/** Response from updating a campaign */
export type UpdateClaimCampaignResponse = Omit<UpdateClaimCampaignContractResponse, 'result'> & {
  result: Omit<UpdateClaimCampaignContractResponse['result'], 'campaign'> & {
    campaign: ClaimCampaign;
  };
};

/** Response from deleting a campaign */
export type DeleteClaimCampaignResponse =
  paths['/api/lux-loyalty/claims/campaigns/{campaignId}']['delete']['responses']['200']['content']['application/json'];

/** Body for admin enrolling customers into a MATCH campaign (1–500 ids per request) */
export type AdminEnrollCustomersBody =
  paths['/api/lux-loyalty/claims/campaigns/{campaignId}/admin-enroll']['post']['requestBody']['content']['application/json'];
export type AdminEnrollCustomersResponse =
  paths['/api/lux-loyalty/claims/campaigns/{campaignId}/admin-enroll']['post']['responses']['200']['content']['application/json'];
export type AdminEnrollOutcome = AdminEnrollCustomersResponse['result']['results'][number]['outcome'];

/** Body for admin removing customers from a MATCH campaign (1–500 ids per request) */
export type AdminRemoveCustomersBody =
  paths['/api/lux-loyalty/claims/campaigns/{campaignId}/admin-remove']['post']['requestBody']['content']['application/json'];
export type AdminRemoveCustomersResponse =
  paths['/api/lux-loyalty/claims/campaigns/{campaignId}/admin-remove']['post']['responses']['200']['content']['application/json'];
export type AdminRemoveOutcome = AdminRemoveCustomersResponse['result']['results'][number]['outcome'];

/** Response from uploading codes */
export interface UploadClaimCodesResponse {
  status: number;
  message: string;
  result: {
    requestedCount: number;
    createdCount: number;
    duplicatesSkipped: number;
  };
}

/** Response from listing codes */
export interface ListClaimCodesResponse {
  status: number;
  message: string;
  result: {
    codes: ClaimCode[];
    summary: Record<ClaimCodeStatus, number>;
    pagination: {
      total: number;
      limit: number;
      offset: number;
    };
  };
}

/** Response from listing claim records */
export interface ListClaimRecordsResponse {
  status: number;
  message: string;
  result: {
    claims: ClaimRecord[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
    };
  };
}

// ---------------------------------------------------------------------------
// Internal Claim (admin submit on behalf of customer)
// ---------------------------------------------------------------------------

/** Individual action result in the internal claim response */
export interface InternalClaimActionResult {
  type: ClaimActionType;
  status: string;
  result?: Record<string, unknown> | null;
  errorCode?: string | null;
  errorMessage?: string | null;
}

/** Response from submitting an internal claim */
export interface InternalSubmitClaimResponse {
  status: number;
  message: string;
  result: {
    claimRecordId: string;
    claimed: boolean;
    actions: InternalClaimActionResult[];
  };
}

// ---------------------------------------------------------------------------
// Code Management (admin update / delete)
// ---------------------------------------------------------------------------

/** Body for updating a claim code */
export type UpdateClaimCodeBody =
  paths['/api/lux-loyalty/claims/campaigns/{campaignId}/codes/{codeId}']['patch']['requestBody']['content']['application/json'];

/** Response from updating a claim code */
export interface UpdateClaimCodeResponse {
  status: number;
  message: string;
  result: {
    code: ClaimCode;
  };
}

/** Response from deleting a claim code */
export interface DeleteClaimCodeResponse {
  status: number;
  message: string;
}

/** Response from test-match-check dry-run */
export interface TestMatchCheckResponse {
  status: number;
  message: string;
  result: {
    rowCount: number;
    rows: Record<string, unknown>[];
  };
}

// ---------------------------------------------------------------------------
// Bulk claim UI types
// ---------------------------------------------------------------------------

export interface ClaimCsvRow {
  customerId: string;
  code?: string;
}

export interface BulkClaimRowResult {
  customerId: string;
  success: boolean;
  claimRecordId?: string;
  error?: string;
}

export interface BulkClaimResult {
  totalProcessed: number;
  successCount: number;
  failedCount: number;
  results: Array<BulkClaimRowResult>;
}

type RequestOptions = {
  signal?: AbortSignal;
};

/**
 * Lists all claim campaigns with optional filters and pagination.
 */
export async function listClaimCampaigns(
  params: ListClaimCampaignsParams,
  options: RequestOptions = {},
): Promise<ListClaimCampaignsResponse> {
  const query = qs.stringify(params, { skipNulls: true });
  return request(`${BASE_PATH}/claims/campaigns/all?${query}`, {
    ...options,
    method: 'GET',
  });
}

/**
 * Creates a new claim campaign.
 */
export async function createClaimCampaign(body: CreateClaimCampaignBody): Promise<CreateClaimCampaignResponse> {
  return request(`${BASE_PATH}/claims/campaigns`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Fetches a single claim campaign by ID.
 */
export async function getClaimCampaign(campaignId: string): Promise<GetClaimCampaignResponse> {
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}`, {
    method: 'GET',
  });
}

/**
 * Updates an existing claim campaign.
 */
export async function updateClaimCampaign(
  campaignId: string,
  body: UpdateClaimCampaignBody,
): Promise<UpdateClaimCampaignResponse> {
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Deletes a claim campaign and all related data.
 */
export async function deleteClaimCampaign(campaignId: string): Promise<DeleteClaimCampaignResponse> {
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}`, {
    method: 'DELETE',
  });
}

/**
 * Uploads claim codes to a campaign.
 */
export async function uploadClaimCodes(
  campaignId: string,
  body: UploadClaimCodesBody,
): Promise<UploadClaimCodesResponse> {
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}/codes`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Lists codes for a campaign with optional status filter and pagination.
 */
export async function listClaimCodes(
  campaignId: string,
  params: ListClaimCodesParams,
  options: RequestOptions = {},
): Promise<ListClaimCodesResponse> {
  const query = qs.stringify(params, { skipNulls: true });
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}/codes?${query}`, {
    ...options,
    method: 'GET',
  });
}

/**
 * Lists claim records for a campaign with optional status filter and pagination.
 */
export async function listClaimRecords(
  campaignId: string,
  params: ListClaimRecordsParams,
  options: RequestOptions = {},
): Promise<ListClaimRecordsResponse> {
  const query = qs.stringify(params, { skipNulls: true });
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}/claims?${query}`, {
    ...options,
    method: 'GET',
  });
}

/**
 * Submits a claim on behalf of a customer (admin internal claim).
 */
export async function submitInternalClaim(
  campaignId: string,
  body: AdminSubmitClaimRequestBody,
): Promise<InternalSubmitClaimResponse> {
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}/admin-claim`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Updates a claim code for a campaign (admin).
 */
export async function updateClaimCode(
  campaignId: string,
  codeId: string,
  body: UpdateClaimCodeBody,
): Promise<UpdateClaimCodeResponse> {
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}/codes/${codeId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Deletes a claim code from a campaign (admin).
 */
export async function deleteClaimCode(campaignId: string, codeId: string): Promise<DeleteClaimCodeResponse> {
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}/codes/${codeId}`, {
    method: 'DELETE',
  });
}

/**
 * Enrols customers into a MATCH campaign on their behalf (admin). Accepts 1–500 customer ids and
 * returns one outcome per customer. Safe to resend: customers already enrolled are reported, not changed.
 */
export async function adminEnrollCustomers(
  campaignId: string,
  body: AdminEnrollCustomersBody,
  options: RequestOptions = {},
): Promise<AdminEnrollCustomersResponse> {
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}/admin-enroll`, {
    ...options,
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Removes customers from a MATCH campaign (admin). Accepts 1–500 customer ids and returns one
 * outcome per customer. Fulfilled enrolments are left as they are.
 */
export async function adminRemoveCustomers(
  campaignId: string,
  body: AdminRemoveCustomersBody,
  options: RequestOptions = {},
): Promise<AdminRemoveCustomersResponse> {
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}/admin-remove`, {
    ...options,
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Runs a dry-run match-check with the given BQ query and eligibility checks.
 */
export async function testMatchCheck(body: TestMatchCheckBody): Promise<TestMatchCheckResponse> {
  return request(`${BASE_PATH}/claims/campaigns/test-match-check`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Lists every claim campaign, unfiltered. Typed from the generated contract, unlike
 * {@link listClaimCampaigns}.
 */
export async function getAllClaimCampaigns(): Promise<ListCampaignsResponse> {
  return request(`${BASE_PATH}/claims/campaigns/all`, { method: 'GET' });
}

/**
 * Submits a claim on behalf of a customer. Same endpoint as {@link submitInternalClaim}, typed from
 * the generated contract.
 */
export async function adminClaim(
  campaignId: string,
  params: AdminSubmitClaimRequestBody,
): Promise<AdminSubmitClaimResponse> {
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}/admin-claim`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function listApprovalRequests(
  campaignId: string,
  params?: ListApprovalRequestsParams,
  options: RequestOptions = {},
): Promise<ListApprovalRequestsResponse> {
  const query = params
    ? `?${new URLSearchParams(Object.entries(params).filter(([, v]) => v != null) as Array<[string, string]>)}`
    : '';
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}/approval-requests${query}`, {
    ...options,
    method: 'GET',
  });
}

export async function getApprovalRequest(
  requestId: string,
  options: RequestOptions = {},
): Promise<GetApprovalRequestResponse> {
  return request(`${BASE_PATH}/claims/approval-requests/${requestId}`, {
    ...options,
    method: 'GET',
  });
}

export async function approveApprovalRequest(
  requestId: string,
  body: ApproveApprovalRequestBody,
): Promise<ApproveApprovalRequestResponse> {
  return request(`${BASE_PATH}/claims/approval-requests/${requestId}/approve`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function rejectApprovalRequest(
  requestId: string,
  body: RejectApprovalRequestBody,
): Promise<RejectApprovalRequestResponse> {
  return request(`${BASE_PATH}/claims/approval-requests/${requestId}/reject`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}
